<script setup>
import getUuid from 'widget/helpers/uuid';
import { ref, onMounted, onBeforeUnmount } from 'vue';
import WaveSurfer from 'wavesurfer.js';
import RecordPlugin from 'wavesurfer.js/dist/plugins/record.js';
import { format, intervalToDuration } from 'date-fns';
import { convertAudio } from './utils/mp3ConversionUtils';
import { getBrandColorHex } from 'shared/helpers/brandColor';

const props = defineProps({
  audioRecordFormat: {
    type: String,
    required: true,
  },
});

const emit = defineEmits([
  'recorderProgressChanged',
  'finishRecord',
  'pause',
  'play',
]);

const waveformContainer = ref(null);
const wavesurfer = ref(null);
const record = ref(null);
const isRecording = ref(false);
const isPlaying = ref(false);
const hasRecording = ref(false);
const isDestroyed = ref(false);
const isInitialized = ref(false);

const formatTimeProgress = time => {
  const duration = intervalToDuration({ start: 0, end: time });
  return format(
    new Date(0, 0, 0, 0, duration.minutes, duration.seconds),
    'mm:ss'
  );
};

const resolveWaveColor = () => getBrandColorHex() || '#1f93ff';

const initWaveSurfer = () => {
  // Guard against container not being available or already initialized
  if (!waveformContainer.value || isDestroyed.value || isInitialized.value) {
    return;
  }

  try {
    wavesurfer.value = WaveSurfer.create({
      container: waveformContainer.value,
      waveColor: resolveWaveColor(),
      progressColor: '#6E6F73',
      height: 100,
      barWidth: 2,
      barGap: 1,
      barRadius: 2,
      plugins: [
        RecordPlugin.create({
          scrollingWaveform: true,
          renderRecordedAudio: false,
        }),
      ],
    });

    isInitialized.value = true;

    wavesurfer.value.on('pause', () => {
      if (!isDestroyed.value) emit('pause');
    });
    wavesurfer.value.on('play', () => {
      if (!isDestroyed.value) emit('play');
    });

    record.value = wavesurfer.value.plugins[0];

    wavesurfer.value.on('finish', () => {
      if (!isDestroyed.value) {
        isPlaying.value = false;
      }
    });

    record.value.on('record-end', async blob => {
      // Guard against component being destroyed during async operation
      if (isDestroyed.value) return;

      const audioUrl = URL.createObjectURL(blob);

      let audioBlob = blob;
      let fileName = `${getUuid()}.webm`;
      let fileType = blob.type || 'audio/webm';

      // Try to convert to MP3, but use original blob if conversion fails
      try {
        audioBlob = await convertAudio(blob, props.audioRecordFormat);
        fileName = `${getUuid()}.mp3`;
        fileType = props.audioRecordFormat;
      } catch {
        // Use the original blob format if conversion fails
      }

      // Check again after async operation
      if (isDestroyed.value) return;

      const file = new File([audioBlob], fileName, {
        type: fileType,
      });

      if (wavesurfer.value && !isDestroyed.value) {
        // load() returns a Promise - catch rejections
        wavesurfer.value.load(audioUrl).catch(() => {
          // Ignore load errors if component is being destroyed
        });

        emit('finishRecord', {
          name: file.name,
          type: file.type,
          size: file.size,
          file,
        });
        hasRecording.value = true;
        isRecording.value = false;
      }
    });

    record.value.on('record-progress', time => {
      if (!isDestroyed.value) {
        emit('recorderProgressChanged', formatTimeProgress(time));
      }
    });
  } catch {
    // Ignore initialization errors
    isInitialized.value = false;
  }
};

const stopRecording = () => {
  if (isRecording.value) {
    record.value.stopRecording();
    isRecording.value = false;
  }
};

const startRecording = () => {
  record.value.startRecording();
  isRecording.value = true;
};

const playPause = () => {
  if (hasRecording.value) {
    wavesurfer.value.playPause();
    isPlaying.value = !isPlaying.value;
  }
};

onMounted(() => {
  initWaveSurfer();
  // Only start recording if wavesurfer was successfully initialized
  if (wavesurfer.value && record.value) {
    startRecording();
  }
});

const destroyWaveSurfer = () => {
  const ws = wavesurfer.value;
  const rec = record.value;

  // Clear refs immediately to prevent any further access
  wavesurfer.value = null;
  record.value = null;

  if (!ws) return;

  // Use setTimeout to defer destruction to next tick
  // This allows pending async operations to complete or be properly cancelled
  setTimeout(async () => {
    try {
      ws.unAll();
    } catch {
      // Ignore unAll errors
    }

    // Stop recording if active
    try {
      if (rec && typeof rec.isRecording === 'function' && rec.isRecording()) {
        rec.stopRecording();
      }
    } catch {
      // Ignore stopRecording errors
    }

    // destroy() can throw both sync and async errors - wrap in try-catch
    try {
      await ws.destroy();
    } catch {
      // Ignore all destroy errors - component is already unmounted
    }
  }, 0);
};

onBeforeUnmount(() => {
  isDestroyed.value = true;
  destroyWaveSurfer();
});

defineExpose({ playPause, stopRecording, record });
</script>

<template>
  <div ref="waveformContainer" class="w-full p-1" />
</template>
